import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useSchemaOrg } from '@/hooks/useSchemaOrg';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 韩漫网站',
  url: 'https://www.example.com/about',
  description: '了解韩漫网站的品牌故事、理念、口碑评价和联系方式。',
  inLanguage: 'zh-CN',
  isPartOf: {
    '@type': 'WebSite',
    name: '韩漫网站',
    url: 'https://www.example.com',
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.example.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
      },
    ],
  },
};

export default function AboutPage() {
  useSchemaOrg(aboutSchema);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9p1q1iqs38sj6jc7hjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.meta as Record<string, string>)?.detail
          || (parsed?.message as string)
          || responseText
          || '提交失败，请稍后再试';
        if (String(serverMsg).includes('spam') || String(serverMsg).includes('form data is spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(String(serverMsg));
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };
  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <span>/</span>
            <span className="text-foreground-800 font-medium">关于我们</span>
          </nav>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
            关于我们
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            了解韩漫网站背后的故事
          </p>
        </div>
      </div>

      <section className="max-w-4xl mx-auto px-4 md:px-6 py-14">
        <div className="mb-16">
          <div className="aspect-[21/9] rounded-xl overflow-hidden bg-background-100 mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20artistic%20illustration%20of%20a%20cozy%20reading%20room%20with%20books%20and%20warm%20lighting%2C%20creative%20editorial%20office%20space%2C%20warm%20coral%20and%20cream%20color%20palette%2C%20artistic%20stylized%20illustration%2C%20clean%20illustration%20style%2C%20modern%20workspace%20aesthetic%2C%20high%20quality%20digital%20art%2C%20no%20text&width=1200&height=500&seq=about-hero-manhwa&orientation=landscape"
              alt="韩漫网站品牌形象"
              className="w-full h-full object-cover"
            />
          </div>

          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
            品牌故事
          </h2>
          <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
            <p>
              韩漫网站成立于<strong>2020年</strong>，由一群热爱韩国漫画的资深读者和内容编辑共同创立。我们发现在当时的互联网环境中，高质量的韩国漫画中文资源非常稀缺，读者往往需要在多个凌乱的网站中苦苦寻找，而且翻译质量和阅读体验参差不齐。
            </p>
            <p>
              于是，我们决定打造一个专注于<strong>高质量的韩国漫画聚合平台</strong>。与大多数依赖算法推荐的内容平台不同，韩漫网站坚持<strong>编辑精选</strong>模式——我们的编辑团队会仔细甄选每一部上架的漫画，确保内容在画风、故事性和翻译质量上都达到我们的高标准。
            </p>
            <p>
              我们创新性地引入了<strong>下拉式韩漫阅读</strong>模式，完美适配手机竖屏浏览习惯，让读者可以像刷社交媒体一样轻松愉快地阅读漫画。这种流畅的阅读体验，迅速赢得了大量读者的喜爱。
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
          <div className="p-6 rounded-xl bg-background-100 text-center">
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
              <i className="ri-user-heart-line text-xl"></i>
            </div>
            <div className="text-2xl font-bold text-foreground-900 mb-1">500万+</div>
            <p className="text-xs text-foreground-500">月活跃读者</p>
          </div>
          <div className="p-6 rounded-xl bg-background-100 text-center">
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
              <i className="ri-book-open-line text-xl"></i>
            </div>
            <div className="text-2xl font-bold text-foreground-900 mb-1">2000+</div>
            <p className="text-xs text-foreground-500">精选漫画作品</p>
          </div>
          <div className="p-6 rounded-xl bg-background-100 text-center">
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 mb-3">
              <i className="ri-star-line text-xl"></i>
            </div>
            <div className="text-2xl font-bold text-foreground-900 mb-1">4.6/5</div>
            <p className="text-xs text-foreground-500">读者平均评分</p>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
            我们的理念
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 rounded-xl bg-background-100 flex gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-shield-check-line text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">质量优先</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  编辑团队严格把关每一部作品，确保画风精美、故事吸引人、翻译准确流畅。
                </p>
              </div>
            </div>
            <div className="p-5 rounded-xl bg-background-100 flex gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                <i className="ri-smartphone-line text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">极致体验</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  下拉式阅读模式专为移动端优化，流畅丝滑，让阅读漫画成为一种享受。
                </p>
              </div>
            </div>
            <div className="p-5 rounded-xl bg-background-100 flex gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 flex-shrink-0">
                <i className="ri-price-tag-3-line text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">完全免费</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  坚持免费阅读模式，让每一位韩漫爱好者都能无障碍地享受精彩内容。
                </p>
              </div>
            </div>
            <div className="p-5 rounded-xl bg-background-100 flex gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-rocket-line text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1">及时更新</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  每日追踪韩国最新发布，热门连载作品24-48小时内同步更新中文版本。
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
            读者口碑
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 rounded-xl bg-background-100">
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <div key={star} className="w-4 h-4 flex items-center justify-center text-amber-400">
                    <i className="ri-star-fill text-sm"></i>
                  </div>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                "终于找到一个不需要翻墙就能看韩漫的好地方了！翻译质量真的很棒，读起来很顺畅。下拉式阅读在手机上体验太好了。"
              </p>
              <p className="text-xs text-foreground-400">—— 漫画迷小王</p>
            </div>
            <div className="p-5 rounded-xl bg-background-100">
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <div key={star} className="w-4 h-4 flex items-center justify-center text-amber-400">
                    <i className="ri-star-fill text-sm"></i>
                  </div>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                "以前在别的网站看韩漫，广告多得烦人。韩漫网站很干净，而且更新速度超快，基本韩国一出我们这就能看到了！"
              </p>
              <p className="text-xs text-foreground-400">—— 追漫达人小李</p>
            </div>
            <div className="p-5 rounded-xl bg-background-100">
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <div key={star} className="w-4 h-4 flex items-center justify-center text-amber-400">
                    <i className="ri-star-fill text-sm"></i>
                  </div>
                ))}
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                "编辑精选确实不一样，推荐的每一部都好看！不像算法推荐那样全是套路化的作品，能感受到编辑的用心。"
              </p>
              <p className="text-xs text-foreground-400">—— 资深读者张女士</p>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
            联系我们
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">电子邮箱</p>
                  <p className="text-sm font-medium text-foreground-800">contact@example.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-global-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">官方网站</p>
                  <p className="text-sm font-medium text-foreground-800">www.example.com</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 flex-shrink-0">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">微信公众号</p>
                  <p className="text-sm font-medium text-foreground-800">韩漫网站</p>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:text-primary-600 hover:bg-primary-100 transition-colors cursor-pointer">
                  <i className="ri-weibo-line text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:text-primary-600 hover:bg-primary-100 transition-colors cursor-pointer">
                  <i className="ri-bilibili-line text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:text-primary-600 hover:bg-primary-100 transition-colors cursor-pointer">
                  <i className="ri-tiktok-line text-lg"></i>
                </a>
              </div>
            </div>

            <form
              className="space-y-4 p-6 rounded-xl bg-background-100"
              data-readdy-form="true"
              onSubmit={handleSubmit}
            >
              <h3 className="text-sm font-semibold text-foreground-900 mb-2">给我们留言</h3>
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="您的姓名"
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="您的邮箱"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="请输入您的留言（不超过500字）"
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                ></textarea>
              </div>
              <div className="honeypot-field">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              {formStatus === 'success' && (
                <p className="text-sm text-accent-600 font-medium">感谢您的留言！我们会尽快回复。</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 font-medium">{formError}</p>
              )}
              <button
                type="submit"
                className="w-full px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                发送留言
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}